import {useEffect, useState} from 'react'
import {FileSpreadsheet, Database, Mail} from 'lucide-react'
import {Subheading} from '@/components/tailwind/heading'
import {Text} from '@/components/tailwind/text'
import {Button} from '@/components/tailwind/button'
import {RadioGroup, RadioField, Radio} from '@/components/tailwind/radio'
import {Checkbox, CheckboxField} from '@/components/tailwind/checkbox'
import {Label} from '@/components/tailwind/fieldset'
import {useAdminTranslation} from '@/i18n/hooks'
import {useAuth} from '@/context/use-auth'
import {useToast} from '@/hooks/use-toast'
import LoadingSpinner from '@/components/loader-spinner'
import {downloadExcelExport, downloadSqlExport, sendExportsByEmail, SendExportsByEmailError} from '@/services/export-services'
import type {SqlExportFormat} from '@/services/export-services'
import {getEmailCapability} from '@/services/email-config-services'
import {EMAIL_CONFIG_CHANGED_EVENT} from '@/lib/email-config-events'

export function SectionExports() {
    const {t} = useAdminTranslation()
    const {token} = useAuth()
    const {toast} = useToast()

    const [excelDownloading, setExcelDownloading] = useState(false)
    const [sqlDownloading, setSqlDownloading] = useState(false)
    const [sqlFormat, setSqlFormat] = useState<SqlExportFormat>('full')
    const [emailIncludeExcel, setEmailIncludeExcel] = useState(true)
    const [emailIncludeSql, setEmailIncludeSql] = useState(false)
    const [emailSqlFormat, setEmailSqlFormat] = useState<SqlExportFormat>('full')
    const [emailSending, setEmailSending] = useState(false)
    const [emailCapabilityAvailable, setEmailCapabilityAvailable] = useState(false)

    useEffect(() => {
        if (!token) return
        let cancelled = false

        function refreshCapability() {
            getEmailCapability(token)
                .then((result) => {
                    if (cancelled) return
                    setEmailCapabilityAvailable(result?.available === true)
                })
                .catch(() => {
                    if (cancelled) return
                    setEmailCapabilityAvailable(false)
                })
        }

        refreshCapability()
        window.addEventListener(EMAIL_CONFIG_CHANGED_EVENT, refreshCapability)
        return () => {
            cancelled = true
            window.removeEventListener(EMAIL_CONFIG_CHANGED_EVENT, refreshCapability)
        }
    }, [token])

    async function handleExcelDownload() {
        if (!token) return

        setExcelDownloading(true)
        try {
            await downloadExcelExport(token)
            toast({
                title: t('adminSettings.exports.success'),
            })
        } catch (_error) {
            toast({
                title: t('adminSettings.exports.error'),
                variant: 'destructive',
            })
        } finally {
            setExcelDownloading(false)
        }
    }

    async function handleSendByEmail() {
        if (!token) return
        if (!emailIncludeExcel && !emailIncludeSql) return

        setEmailSending(true)
        try {
            await sendExportsByEmail(token, {
                includeExcel: emailIncludeExcel,
                includeSql: emailIncludeSql,
                sqlFormat: emailIncludeSql ? emailSqlFormat : undefined,
            })
            toast({
                title: t('adminSettings.exports.sendByEmail.success'),
            })
        } catch (error) {
            let title = t('adminSettings.exports.sendByEmail.error')
            if (error instanceof SendExportsByEmailError) {
                if (error.code === 'RECIPIENT_EMAIL_MISSING') {
                    title = t('adminSettings.exports.sendByEmail.noUserEmail')
                } else if (error.code === 'EMAIL_DISABLED') {
                    title = t('adminSettings.exports.sendByEmail.emailDisabled')
                }
            }
            toast({
                title,
                variant: 'destructive',
            })
        } finally {
            setEmailSending(false)
        }
    }

    async function handleSqlDownload() {
        if (!token) return

        setSqlDownloading(true)
        try {
            await downloadSqlExport(token, sqlFormat)
            toast({
                title: t('adminSettings.exports.success'),
            })
        } catch (_error) {
            toast({
                title: t('adminSettings.exports.error'),
                variant: 'destructive',
            })
        } finally {
            setSqlDownloading(false)
        }
    }

    return (
        <div>
            <Subheading>{t('adminSettings.exports.title')}</Subheading>
            <Text className="mt-1">{t('adminSettings.exports.description')}</Text>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Excel Export Card */}
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <FileSpreadsheet className="h-5 w-5 text-green-600 dark:text-green-400" />
                        <Text className="font-medium">{t('adminSettings.exports.excelExport.title')}</Text>
                    </div>
                    <Text className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                        {t('adminSettings.exports.excelExport.description')}
                    </Text>
                    <Button
                        outline
                        onClick={handleExcelDownload}
                        disabled={excelDownloading}
                        className="w-full sm:w-auto"
                    >
                        {excelDownloading ? (
                            <span className="flex items-center gap-2">
                                <LoadingSpinner size={16} />
                                {t('adminSettings.exports.downloading')}
                            </span>
                        ) : (
                            t('adminSettings.exports.excelExport.button')
                        )}
                    </Button>
                </div>

                {/* SQL Export Card */}
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <Text className="font-medium">{t('adminSettings.exports.sqlExport.title')}</Text>
                    </div>
                    <Text className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                        {t('adminSettings.exports.sqlExport.description')}
                    </Text>

                    <div className="mb-4">
                        <Text className="text-sm font-medium mb-2">{t('adminSettings.exports.sqlExport.formatLabel')}</Text>
                        <RadioGroup value={sqlFormat} onChange={(value: string) => setSqlFormat(value as SqlExportFormat)}>
                            <RadioField>
                                <Radio value="full" color="blue" />
                                <Label>{t('adminSettings.exports.sqlExport.formatFull')}</Label>
                            </RadioField>
                            <RadioField>
                                <Radio value="structure" color="blue" />
                                <Label>{t('adminSettings.exports.sqlExport.formatStructure')}</Label>
                            </RadioField>
                            <RadioField>
                                <Radio value="data" color="blue" />
                                <Label>{t('adminSettings.exports.sqlExport.formatData')}</Label>
                            </RadioField>
                        </RadioGroup>
                    </div>

                    <Button
                        outline
                        onClick={handleSqlDownload}
                        disabled={sqlDownloading}
                        className="w-full sm:w-auto"
                    >
                        {sqlDownloading ? (
                            <span className="flex items-center gap-2">
                                <LoadingSpinner size={16} />
                                {t('adminSettings.exports.downloading')}
                            </span>
                        ) : (
                            t('adminSettings.exports.sqlExport.button')
                        )}
                    </Button>
                </div>
            </div>

            {emailCapabilityAvailable && (
            <div className="mt-4 rounded-lg border border-zinc-200 dark:border-zinc-700 p-5">
                <div className="flex items-center gap-2 mb-2">
                    <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    <Text className="font-medium">{t('adminSettings.exports.sendByEmail.title')}</Text>
                </div>
                <Text className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                    {t('adminSettings.exports.sendByEmail.description')}
                </Text>

                <div className="space-y-2 mb-4">
                    <CheckboxField>
                        <Checkbox
                            name="email-include-excel"
                            checked={emailIncludeExcel}
                            onChange={setEmailIncludeExcel}
                        />
                        <Label>{t('adminSettings.exports.sendByEmail.includeExcel')}</Label>
                    </CheckboxField>
                    <CheckboxField>
                        <Checkbox
                            name="email-include-sql"
                            checked={emailIncludeSql}
                            onChange={setEmailIncludeSql}
                        />
                        <Label>{t('adminSettings.exports.sendByEmail.includeSql')}</Label>
                    </CheckboxField>
                </div>

                {emailIncludeSql && (
                    <div className="mb-4 ml-6">
                        <Text className="text-sm font-medium mb-2">{t('adminSettings.exports.sqlExport.formatLabel')}</Text>
                        <RadioGroup value={emailSqlFormat} onChange={(value: string) => setEmailSqlFormat(value as SqlExportFormat)}>
                            <RadioField>
                                <Radio value="full" color="blue" />
                                <Label>{t('adminSettings.exports.sqlExport.formatFull')}</Label>
                            </RadioField>
                            <RadioField>
                                <Radio value="structure" color="blue" />
                                <Label>{t('adminSettings.exports.sqlExport.formatStructure')}</Label>
                            </RadioField>
                            <RadioField>
                                <Radio value="data" color="blue" />
                                <Label>{t('adminSettings.exports.sqlExport.formatData')}</Label>
                            </RadioField>
                        </RadioGroup>
                    </div>
                )}

                <Button
                    outline
                    onClick={handleSendByEmail}
                    disabled={emailSending || (!emailIncludeExcel && !emailIncludeSql)}
                    className="w-full sm:w-auto"
                >
                    {emailSending ? (
                        <span className="flex items-center gap-2">
                            <LoadingSpinner size={16} />
                            {t('adminSettings.exports.sendByEmail.sending')}
                        </span>
                    ) : (
                        t('adminSettings.exports.sendByEmail.button')
                    )}
                </Button>
            </div>
            )}
        </div>
    )
}
